// src/context/CartContext.js
import React, { createContext, useState, useCallback, useContext, useEffect } from 'react';
import { api } from '../services/api';
import { AuthContext } from './AuthContext';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated, setPendingCartAction, setRedirectPath } = useContext(AuthContext);

  const getCart = useCallback(async () => {
    try {
      console.log('🔄 getCart appelé');
      setLoading(true);
      const response = await api.get('/cart/');
      console.log('📥 Réponse getCart:', response);
      const data = await response.json();
      console.log('📥 Données getCart:', data);
      setCart(data);
      console.log('✅ Panier mis à jour dans l\'état');
    } catch (err) {
      console.error('❌ Erreur lors de la récupération du panier:', err);
      setError('Impossible de charger le panier');
    } finally {
      setLoading(false);
    }
  }, []);

  const addToCart = useCallback(async (cartData) => {
    try {
      // Si l'utilisateur n'est pas authentifié, stocker l'action et rediriger vers la connexion
      if (!isAuthenticated) {
        setPendingCartAction({
          type: 'addToCart',
          data: cartData
        });
        setRedirectPath('/cart');
        // Rediriger vers la page de connexion
        window.location.href = '/login';
        return;
      }

      setLoading(true);
      setError(null);

      if ((cartData.quantity_adult || 0) + (cartData.quantity_child || 0) <= 0) {
        throw new Error('Veuillez sélectionner au moins un billet');
      }

      const response = await api.post('/cart/', {
        event_id: cartData.event_id,
        quantity_adult: cartData.quantity_adult || 0,
        quantity_child: cartData.quantity_child || 0
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'ajout au panier');
      }

      await getCart();
    } catch (err) {
      console.error('Erreur lors de l\'ajout au panier:', err);
      setError(err.message || 'Erreur lors de l\'ajout au panier');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCart, isAuthenticated, setPendingCartAction, setRedirectPath]);

  const addPassToCart = useCallback(async (passData) => {
    console.log('🔍 addPassToCart appelé avec:', passData);
    console.log('🔍 isAuthenticated:', isAuthenticated);
    
    try {
      // Si l'utilisateur n'est pas authentifié, stocker l'action et rediriger vers la connexion
      if (!isAuthenticated) {
        console.log('❌ Utilisateur non authentifié, redirection vers login');
        setPendingCartAction({
          type: 'addPassToCart',
          data: passData
        });
        setRedirectPath('/cart');
        // Rediriger vers la page de connexion
        window.location.href = '/login';
        return;
      }

      console.log('✅ Utilisateur authentifié, ajout au panier...');
      setLoading(true);
      setError(null);

      if ((passData.quantity_adult || 0) + (passData.quantity_child || 0) <= 0) {
        throw new Error('Veuillez sélectionner au moins un billet');
      }

      const requestData = {
        pass_id: passData.pass_id,
        quantity_adult: passData.quantity_adult || 0,
        quantity_child: passData.quantity_child || 0,
        selected_events: passData.selected_events || []
      };
      
      console.log('📤 Envoi de la requête:', requestData);
      console.log('📤 URL:', '/add-pass-to-cart/');

      const response = await api.post('/add-pass-to-cart/', requestData);
      
      console.log('📥 Réponse reçue:', response);
      console.log('📥 Status:', response.status);
      console.log('📥 OK:', response.ok);

      if (!response.ok) {
        const error = await response.json();
        console.log('❌ Erreur API:', error);
        throw new Error(error.detail || 'Erreur lors de l\'ajout du pass au panier');
      }

      console.log('✅ Pass ajouté avec succès, rechargement du panier...');
      await getCart();
      console.log('✅ Panier rechargé');
    } catch (err) {
      console.error('❌ Erreur lors de l\'ajout du pass au panier:', err);
      setError(err.message || 'Erreur lors de l\'ajout du pass au panier');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCart, isAuthenticated, setPendingCartAction, setRedirectPath]);

  const updateQuantity = useCallback(async (itemId, quantities) => {
    try {
      setLoading(true);
      setError(null);

      if ((quantities.quantity_adult || 0) + (quantities.quantity_child || 0) <= 0) {
        throw new Error('Veuillez sélectionner au moins un billet');
      }

      const response = await api.put(`/cart/${itemId}/`, {
        quantity_adult: quantities.quantity_adult || 0,
        quantity_child: quantities.quantity_child || 0
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la mise à jour du panier');
      }

      await getCart();
    } catch (err) {
      console.error('Erreur lors de la mise à jour du panier:', err);
      setError(err.message || 'Erreur lors de la mise à jour du panier');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCart]);

  const updatePassQuantity = useCallback(async (itemId, quantities) => {
    try {
      setLoading(true);
      setError(null);

      if ((quantities.quantity_adult || 0) + (quantities.quantity_child || 0) <= 0) {
        throw new Error('Veuillez sélectionner au moins un billet');
      }

      const response = await api.put(`/cart/pass/${itemId}/`, {
        quantity_adult: quantities.quantity_adult || 0,
        quantity_child: quantities.quantity_child || 0
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la mise à jour du pass du panier');
      }

      await getCart();
    } catch (err) {
      console.error('Erreur lors de la mise à jour du pass du panier:', err);
      setError(err.message || 'Erreur lors de la mise à jour du pass du panier');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCart]);

  const removeFromCart = useCallback(async (itemId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.delete(`/cart/${itemId}/`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la suppression du panier');
      }

      await getCart();
    } catch (err) {
      console.error('Erreur lors de la suppression du panier:', err);
      setError(err.message || 'Erreur lors de la suppression du panier');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCart]);

  const removePassFromCart = useCallback(async (itemId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.delete(`/cart/pass/${itemId}/`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la suppression du pass du panier');
      }

      await getCart();
    } catch (err) {
      console.error('Erreur lors de la suppression du pass du panier:', err);
      setError(err.message || 'Erreur lors de la suppression du pass du panier');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCart]);

  const clearCart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.delete('/cart/');

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la vidange du panier');
      }

      setCart(null);
    } catch (err) {
      console.error('Erreur lors de la vidange du panier:', err);
      setError(err.message || 'Erreur lors de la vidange du panier');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour exécuter les actions en attente après authentification
  const executePendingAction = useCallback(async (pendingAction) => {
    if (!pendingAction) return;

    try {
      setLoading(true);
      setError(null);

      switch (pendingAction.type) {
        case 'addToCart':
          await addToCart(pendingAction.data);
          break;
        case 'addPassToCart':
          await addPassToCart(pendingAction.data);
          break;
        default:
          console.warn('Type d\'action en attente non reconnu:', pendingAction.type);
      }
    } catch (err) {
      console.error('Erreur lors de l\'exécution de l\'action en attente:', err);
      setError(err.message || 'Erreur lors de l\'exécution de l\'action en attente');
    } finally {
      setLoading(false);
    }
  }, [addToCart, addPassToCart]);

  // Charger le panier au démarrage si l'utilisateur est authentifié
  useEffect(() => {
    if (isAuthenticated) {
      getCart();
    }
  }, [isAuthenticated, getCart]);

  // Exécuter les actions en attente quand l'utilisateur s'authentifie
  useEffect(() => {
    if (isAuthenticated) {
      // On va utiliser un événement personnalisé pour déclencher l'exécution
      const handlePendingAction = (event) => {
        const { pendingAction } = event.detail;
        if (pendingAction) {
          executePendingAction(pendingAction);
        }
      };
      
      window.addEventListener('pendingActionReady', handlePendingAction);
      
      return () => {
        window.removeEventListener('pendingActionReady', handlePendingAction);
      };
    }
  }, [isAuthenticated, executePendingAction]);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      error,
      getCart,
      addToCart,
      addPassToCart,
      updateQuantity,
      updatePassQuantity,
      removeFromCart,
      removePassFromCart,
      clearCart,
      executePendingAction
    }}>
      {children}
    </CartContext.Provider>
  );
};
